import styled from 'styled-components';
import { useIsFetching } from '@tanstack/react-query';
import { Spacing24, Spacing32, Spacing64 } from 'autogen/design-tokens/tokens';
import type { ProductListPartialViewModel } from 'autogen/swagger/all';
import LoadingContainer from 'components/checkout/components/BasketOverview/components/LoadingContainer';
import Flex from 'components/Layout/components/Flex';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import { breakpoint, sizes } from 'variables';
import ProductListFooter from './components/ProductListFooter';
import ProductListHeader from './components/ProductListHeader';
import ProductListShortcutArrow from './components/ProductListShortcutArrow';
import ProductListSideContainer from './components/ProductListSideContainer';
import ZeroResultProductList from './components/ZeroResultProductList';
import ProductList from './ProductList';
import { useSetProductListTopPosition } from './shared/hooks/useProductListPositionTopPosition';

/* #region styles */
const Grid = styled.div<{ $fullWidth: boolean }>`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing32};

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: ${({ $fullWidth }) => ($fullWidth ? '1fr' : '1fr 4fr')};
    }
`;

const StyledFlexColumn = styled(Flex.Column)`
    min-width: 0;
`;
/* #endregion */

export default function ProductListWithFilters({ model }: { model: ProductListPartialViewModel }) {
    const { productList, showFiltersSide, showMenu } = model;
    const { items, pagingModel, menu } = productList;

    const isLoading = useIsFetching({ queryKey: ['productListPartial'] });
    const hasMenu = showMenu && !!menu;
    const hasSideContainer = showFiltersSide || hasMenu;
    const isZeroFilterResult = !pagingModel.hitsTotal && productList.hasActiveUserFilters;

    const setProductListTopPosition = useSetProductListTopPosition();

    return (
        <TrackingContext context="product_list">
            <Grid $fullWidth={!hasSideContainer}>
                {hasSideContainer && <ProductListSideContainer model={model} />}

                <StyledFlexColumn gap={Spacing24} ref={setProductListTopPosition}>
                    <ProductListHeader model={model} />
                    <Flex.Column gap={{ xs: Spacing32, lg: Spacing64 }}>
                        <LoadingContainer isLoading={!!isLoading}>
                            {isZeroFilterResult ? <ZeroResultProductList /> : <ProductList items={items} />}
                        </LoadingContainer>

                        <ProductListFooter model={productList} />
                    </Flex.Column>
                    <ProductListShortcutArrow />
                </StyledFlexColumn>
            </Grid>
        </TrackingContext>
    );
}
